import type { ReactNode } from "react";
// Private-only lines (financials) for the PDFs AJ sends out. This import is an ALIAS
// (vite.config.ts): `vite build --mode private` resolves it to resume-private.tsx, every
// other build to resume-private.public.ts, which is empty. The public build therefore
// never READS the private file, so its lines can't reach the bundle OR the published
// source maps (which embed original source). Export with scripts/export-resume.sh.
import { PRIVATE_LAB } from "@resume-private";

/** The resume copy — VERBATIM from "AJG Resume 2026" (+ edits AJ has asked for since).
 *  Single source for both /resume (sections/Resume.tsx) and the site's work-history
 *  section (sections/Work.tsx). Edit word for word, here only. */

export const SUMMARY_LEAD =
  "Early-stage PLG strategic product leader with strong sales and technical acumen";
export const SUMMARY_REST = "seeking early GTM motions, complex problems, and novel technology.";

export const EDUCATION = {
  school: "Texas A&M University – Mays Business School",
  place: "College st., tx",
  degree: "Bachelors of Business Administration in Marketing with a Sales Concentration",
};

export type Role = { when: string; title: string; bullets: ReactNode[] };
export type Employer = { key: string; logo: string; logoAlt: string; logoH: number; accent: string; roles: Role[] };

export const EXPERIENCE: Employer[] = [
  {
    key: "cloudflare",
    logo: "/resume/cloudflare.png",
    logoAlt: "Cloudflare",
    logoH: 22,
    accent: "var(--orange)",
    roles: [
      {
        when: "‘24-now",
        title: "senior product manager - zero trust & sase",
        bullets: [
          "Owns both frontend and backend technical products, along with self-service revenue growth",
          "Drove 70% growth in self-service revenue (previously flat growth for 2+ years)",
          <>
            Owned organization-wide projects like navigation consolidation, and tier-one releases
            covering integrations between major product areas (<em>compute</em> and security
            platform, etc)
          </>,
          "Experienced building with AI, building AI-forward experiences, and building, integrating with, and implementing true full-stack AI products from gateway to model to harness to context.",
        ],
      },
      {
        when: "‘19-24",
        title: "principal specialist - zero trust & sase",
        bullets: [
          "First salesperson leading Cloudflare entry into Zero Trust / SASE market (team now 50+ int’l)",
          <>
            Grew ZTNA Enterprise business from <strong>&lt;$500k to ~$185M ARR</strong> business
          </>,
          "Global ZTNA/SASE expert, led 4,000+ customer conversations, presentations, whiteboard sessions, deployments, and webinars about Zero Trust industry and Cloudflare One",
          "Directly closed $15M+ in revenue, including 80% of all $1M+ opportunities globally",
          "Published author on Cloudflare blog and in Cloudflare reference architectures and dev docs",
        ],
      },
    ],
  },
  {
    key: "tpfl",
    logo: "/resume/turbo.png",
    logoAlt: "Turbo Photo Film Lab",
    logoH: 26,
    accent: "var(--purple)",
    roles: [
      {
        when: "2023-now",
        title: "co-founder, turbo photo film lab",
        bullets: [
          PRIVATE_LAB?.firstBullet ??
            "Developed novel business model for developing 35mm photography film, 5,000+ unique customers, 16,000+ orders, 25,000+ rolls of film processed since Oct. 2023",
          "Built most optimized photo/film lab (probably) on the planet, processing 1,700+ monthly rolls of film with only a single employee and 87% profit margin",
          ...(PRIVATE_LAB?.extraBullets ?? []),
          "Very early comprehensive agentic adoption; business runs with 5+ autonomous agents watching logs, identifying and resolving incidents, responding to email and social media, and more.",
          <>
            Named{" "}
            <a
              href="https://calendar.austinchronicle.com/best-of/2026/services-critics-picks/best-analog-subscription-14117787"
              target="_blank"
              rel="noopener"
            >
              Best of Austin winner (best analog subscription)
            </a>{" "}
            for 2026 by the Austin Chronicle
          </>,
        ],
      },
    ],
  },
  {
    key: "wpengine",
    logo: "/resume/wpengine.png",
    logoAlt: "WP Engine",
    logoH: 18,
    accent: "var(--cyan)",
    roles: [
      {
        when: "17-‘19",
        title: "multiple enterprise sales roles",
        bullets: [
          "Consistent quota attainment, developed new market strategies, first ever data-driven territory model.",
        ],
      },
    ],
  },
];
